import {
  Edit,
} from '@flowio/react-icons';
import React from 'react';
import { Link } from 'react-router';
import { IconButton } from '@flowio/react-button';
import {
  Card, CardHeader, CardContent, CardFooter,
} from '@flowio/react-card';
import { Grid, Column, Row } from '@flowio/react-grid';
import { DisputeDefenseOutcome, DisputeStatus } from '@flowio/api-internal-constants';
import startCase from 'lodash/startCase';
import FormattedDate from '../../../../components/formatted-date';
import Description from '../../../../components/description/description';

import DescriptionGroup from '../../../../components/description/description-group';
import StatusLabel from '../../../../components/status-label/StatusLabel';

import { disputeLabel, editIcon } from './order-disputes.styles';

const displayName = 'OrderDisputes';

const getCodeReason = (dispute: io.flow.internal.v0.models.Dispute) => [dispute.code, dispute.reason].filter(Boolean).join(':');

const getPSPDisputeLink = (
  dispute: io.flow.internal.v0.models.Dispute,
  authorization: io.flow.internal.v0.models.SearchAuthorization,
) => {
  let url = '';
  if (dispute.processor === 'adyen') {
    url = `https://ca-live.adyen.com/ca/ca/accounts/showTx.shtml?pspReference=${dispute.key}&txType=Dispute`;
  } else if (dispute.processor === 'stripe') {
    const {
      payment_intent_id: paymentIntentId,
    } = dispute.details as io.flow.internal.v0.models.DisputeDetailsStripe;
    if (authorization.method === 'Klarna') {
      const account = authorization.processor?.account?.identifier.id;
      url = `https://dashboard.stripe.com/connect/accounts/${account}/payments/${paymentIntentId}`;
    } else {
      url = `https://dashboard.stripe.com/payments/${paymentIntentId}`;
    }
  } else if (dispute.processor === 'paypal') {
    url = `https://www.paypal.com/resolutioncenter/view/${dispute.key}`;
  }
  if (!url) {
    return null;
  }

  return (
    <Link target="_blank" to={url}>
      {dispute.key}
    </Link>
  );
};

const getDisputeStatusIntent = (str: string = ''): string => {
  if ([
    DisputeDefenseOutcome.DEFENDED,
    DisputeStatus.WON,
  ].map((e) => e.toString())
    .includes(str)) {
    return 'positive';
  }

  if ([
    DisputeDefenseOutcome.UNDEFENDABLE,
    DisputeDefenseOutcome.UNDEFENDED,
    DisputeStatus.LOST,
    DisputeStatus.EXPIRED,
  ].map((e) => e.toString())
    .includes(str)) {
    return 'negative';
  }

  if ([DisputeStatus.PENDING]
    .map((e) => e.toString())
    .includes(str)) {
    return 'warning';
  }

  return 'neutral';
};

export const DisputeCard: React.FC<DisputeCardProps> = ({
  handleUpdate = null,
  disputeDetail,
  isFlowEmployee = false,
}) => {
  const { dispute, authorization } = disputeDetail;
  const column1Width = 6;
  let column2Width = 5;
  const column3Width = 1;

  if (!handleUpdate) {
    column2Width = 6;
  }
  return (
    <>
      <Card>
        <CardContent>
          <Grid gutter={false}>
            <Row gutter={false}>
              <Column span={column1Width}>
                <DescriptionGroup spacing={false} className={''}>
                  <Description>
                    <Description.Term className={disputeLabel} content="PSP reference #" />
                    <Description.Definition>
                      {getPSPDisputeLink(dispute, authorization)}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Dispute Date" />
                    <Description.Definition>
                      <FormattedDate utc value={dispute.issued_at} format="MMM DD, YYYY h:mm A" />
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Expiration Date" />
                    <Description.Definition>
                      <FormattedDate utc value={dispute.expires_at} format="MMM DD, YYYY h:mm A" />
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Amount" />
                    <Description.Definition>
                      {dispute.total.amount}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Code and Reason" />
                    <Description.Definition>
                      {getCodeReason(dispute)}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Billable" />
                    <Description.Definition>
                      {startCase(dispute.billable)}
                    </Description.Definition>
                  </Description>
                </DescriptionGroup>
              </Column>
              <Column span={column2Width}>
                <DescriptionGroup spacing={false} className={''}>
                  <Description>
                    <Description.Term className={disputeLabel} content="PSP" />
                    <Description.Definition>
                      {dispute.processor}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Last Updated Date" />
                    <Description.Definition>
                      <FormattedDate utc value={dispute.updated_at} format="MMM DD, YYYY h:mm A" />
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Defended At Date" />
                    { dispute.defended_at && (
                      <Description.Definition>
                        <FormattedDate utc value={dispute.defended_at} format="MMM DD, YYYY h:mm A" />
                      </Description.Definition>
                    )}
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Currency" />
                    <Description.Definition>
                      {dispute.total.currency}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Defense Outcome" />
                    <Description.Definition>
                      <StatusLabel
                        content={startCase(dispute.defense_outcome || '')}
                        intent={getDisputeStatusIntent(dispute.defense_outcome)}
                      />
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Chargeback Category" />
                    <Description.Definition>
                      {startCase(dispute.reporting_category)}
                    </Description.Definition>
                  </Description>
                  <Description>
                    <Description.Term className={disputeLabel} content="Status" />
                    <Description.Definition>
                      <StatusLabel
                        content={startCase(dispute.status)}
                        intent={getDisputeStatusIntent(dispute.status)}
                      />
                    </Description.Definition>
                  </Description>
                </DescriptionGroup>
              </Column>
              {isFlowEmployee && handleUpdate && (
              <Column span={column3Width}>
                <IconButton
                  className={editIcon}
                  onClick={() => handleUpdate(disputeDetail)}
                  variant="outline"
                  icon={Edit}
                  intent="primary"
                />
              </Column>
              )}
            </Row>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

interface OrderDisputesProps {
  handleUpdate: (dispute: io.flow.internal.v0.models.DisputeDetail) => void;
  disputes: io.flow.internal.v0.models.DisputeDetail[];
  isFlowEmployee: boolean;
}

const OrderDisputes: React.FC<OrderDisputesProps> = ({
  handleUpdate = null,
  disputes = [],
  isFlowEmployee = false,
}) => (
  <Card>
    <CardHeader caption="Disputes" />
    <CardContent>
      {disputes.map(
        (dispute_detail) => (
          <DisputeCard
            key={dispute_detail.dispute.id}
            handleUpdate={handleUpdate}
            disputeDetail={dispute_detail}
            isFlowEmployee={isFlowEmployee}
          />
        ),
      )}
    </CardContent>
    <CardFooter />
  </Card>
);

interface DisputeCardProps {
  handleUpdate: ((dispute: io.flow.internal.v0.models.DisputeDetail) => void) | null;
  disputeDetail: io.flow.internal.v0.models.DisputeDetail;
  isFlowEmployee: boolean;
}

OrderDisputes.displayName = displayName;
export default OrderDisputes;
